import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AppBar, Tabs, Tab, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button, Select, MenuItem, InputLabel, FormControl, Chip, OutlinedInput, Box, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { useAuth } from '../../AuthContext';
import configData from '../../configure';
import './UserProfileModule.css';

// ── Detail sub-components (reused as-is) ──
import StudentDetails from '../Students/StudentDetails';
import FacultyDetails from '../Faculty/FacultyDetails';
import StaffDetails from '../Faculty/StaffDetails';

// ── Student-specific tab components ──
import Calendar from '../Students/ClassScheduleCalander';
import Fees from '../Students/FeeDetails';
import Messages from '../Students/Messages';
import ClassSchedule from '../Students/ClassSchedule';
import StudentPerformanceDashboard from '../Students/StudentPerformanceDashboard';

// ── Faculty-specific tab components ──
import ClassesTab from '../Faculty/ClassesTab';
import ClassScheduleTab from '../Faculty/ClassScheduleTab';
import SubordinatesTab from '../Faculty/SubordinatesTab';
import NirvahakStudentsTab from '../Faculty/NirvahakStudentsTab';
// ── Common tab components ──
import DocumentsTab from '../Common/DocumentsTab';

import { MODULES, ACTIONS } from '../../permissionsConfig';

/* ─────────────────────────────────────
   Info-key mapping per user type
   ───────────────────────────────────── */
const INFO_KEY_MAP = {
    STUDENT: 'studentInfo',
    FACULTY: 'facultyInfo',
    STAFF: 'staffInfo',
    ADMIN: 'staffInfo',
};

/* ─────────────────────────────────────
   UserProfileModule
   ───────────────────────────────────── */
function UserProfileModule({ selectedUser: initialUser, onSelectedUserChange, onUserUpdate, orgId, userType, isKvFaculty, onAddClasses, onOpenSubordinate, onDelete }) {
    const { axios, hasPermission, token, userRole } = useAuth();
    const infoKey = INFO_KEY_MAP[userType] || 'staffInfo';

    // Normalize: ensure rawData wrapper exists (FacultyDetails/StaffDetails read from selectedUser.rawData.user)
    const normalizeUser = useCallback((user) => {
        if (!user) return user;
        if (user.rawData) return user; // already normalized
        // The tab data from addTab stores: { user: {...}, facultyInfo: {...}, staffInfo: {...}, ... }
        // Detail components expect: { rawData: { user: {...}, facultyInfo: {...} }, firstName, ... }
        return {
            ...user,
            rawData: {
                user: user.user,
                studentInfo: user.studentInfo,
                facultyInfo: user.facultyInfo,
                staffInfo: user.staffInfo,
                parentInfo: user.parentInfo,
            },
            // Also keep top-level flat fields for fallback access
            firstName: user.user?.firstName || user.firstName,
            lastName: user.user?.lastName || user.lastName,
            email: user.user?.email || user.email,
            phoneNumber: user.user?.phoneNumber || user.phoneNumber,
            gender: user.user?.gender || user.gender,
            dateOfBirth: user.user?.dateOfBirth || user.dateOfBirth,
            userId: user.user?.userId || user.userId,
        };
    }, []);

    const [selectedUser, setSelectedUser] = useState(() => normalizeUser(initialUser));
    const [currentTab, setCurrentTab] = useState(0);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);

    // Classes assignment state for Nirvahak
    const [openClassesModal, setOpenClassesModal] = useState(false);
    const [classes, setClasses] = useState([]);
    const [selectedClasses, setSelectedClasses] = useState([]);

    const rawUser = selectedUser?.rawData?.user || selectedUser?.user || selectedUser || {};
    const firstName = rawUser.firstName || selectedUser?.firstName || '';
    const lastName = rawUser.lastName || selectedUser?.lastName || '';
    const name = `${firstName} ${lastName}`.trim() || rawUser.email || '';
    const email = rawUser.email || '';
    const phoneNumber = rawUser.phoneNumber || '';
    
    const fType = selectedUser?.rawData?.facultyInfo?.facultyType || selectedUser?.facultyInfo?.facultyType || selectedUser?.facultyType || userType;
    const department = selectedUser?.rawData?.user?.additionalAttributes?.assignedClasses || selectedUser?.user?.additionalAttributes?.assignedClasses || selectedUser?.additionalAttributes?.assignedClasses || '';

    const displayClasses = useMemo(() => {
        const validClassNames = new Set(classes.map(c => c.value.toLowerCase().trim()));
        return department && department !== 'Main Region'
            ? department.split(',').map(d => d.trim()).filter(d => validClassNames.has(d.toLowerCase().trim())).join(', ')
            : '';
    }, [department, classes]);

    const getInitials = (fName, lName, mail) => {
        if (fName || lName) {
            return `${(fName || '')[0] || ''}${(lName || '')[0] || ''}`.toUpperCase();
        }
        return (mail || 'US').substring(0, 2).toUpperCase();
    };



    // Load all classes in the organization
    useEffect(() => {
        if (orgId && axios) {
            axios.get(`${configData.GET_CLASSES_BY_ORG}/${orgId}/metadata`)
                .then(res => {
                    const sorted = (res.data || []).map(cls => {
                        const section = (cls.sectionName && cls.sectionName !== 'null' && cls.sectionName !== 'undefined') ? String(cls.sectionName).trim() : '';
                        const label = section ? `${cls.className} ${section}` : `${cls.className || ''}`.trim();
                        return {
                            value: label || 'Unnamed Class',
                            label: label || 'Unnamed Class'
                        };
                    }).sort((a, b) => a.label.localeCompare(b.label));
                    setClasses(sorted);
                })
                .catch(err => console.error(err));
        }
    }, [orgId, axios]);

    const handleOpenClassesModal = () => {
        const validClassNames = new Set(classes.map(c => c.value.toLowerCase().trim()));
        const currentClasses = department && department !== 'Main Region'
            ? department.split(',').map(d => d.trim()).filter(d => validClassNames.has(d.toLowerCase().trim()))
            : [];
        setSelectedClasses(currentClasses);
        setOpenClassesModal(true);
    };

    const handleSaveClasses = async () => {
        setLoading(true);
        try {
            const rawUserObj = selectedUser?.rawData?.user || selectedUser?.user || selectedUser || {};
            const rawInfo = selectedUser?.rawData?.facultyInfo || selectedUser?.facultyInfo || {};
            const userId = rawUserObj.userId || selectedUser?.userId;
            const facultyId = rawInfo.facultyId || selectedUser?.facultyId || selectedUser?.rawData?.facultyInfo?.facultyId;
            const currentTime = new Date().toISOString();

            const userPayload = {
                ...rawUserObj,
                userId,
                orgId: rawUserObj.orgId || orgId,
                userType: rawUserObj.userType || userType,
                roleId: rawUserObj.roleId || `ROLE_${userType}`,
                userStatus: rawUserObj.userStatus || 'ACTIVE',
                lastUpdatedBy: 'system',
                lastUpdatedAt: currentTime,
                addressDetails: rawUserObj.addressDetails || [{ addressType: 'CURRENT' }],
                additionalAttributes: {
                    ...(rawUserObj.additionalAttributes || {}),
                    assignedClasses: selectedClasses.join(', '),
                }
            };

            const facultyPayload = {
                ...rawInfo,
                facultyId,
                userId,
                department: '', // Clear department to favor additionalAttributes
                lastUpdatedBy: 'system',
                lastUpdatedAt: currentTime,
            };

            const updateData = {
                user: userPayload,
                studentInfo: null,
                parentInfo: null,
                staffInfo: null,
                facultyInfo: facultyPayload,
            };

            await axios.put(`${configData.UPDATE_USER}/${userId}`, updateData);
            
            toast.success('Classes successfully assigned to Nirvahak!');
            setOpenClassesModal(false);

            // Notify UserModule and update locally immediately
            const updatedUser = {
                ...selectedUser,
                department: '',
                additionalAttributes: {
                    ...(rawUserObj.additionalAttributes || {}),
                    assignedClasses: selectedClasses.join(', '),
                },
                user: userPayload,
                facultyInfo: facultyPayload,
                rawData: {
                    ...selectedUser.rawData,
                    user: userPayload,
                    facultyInfo: facultyPayload
                }
            };
            if (onSelectedUserChange) onSelectedUserChange(updatedUser);
            if (onUserUpdate) onUserUpdate(updatedUser);
        } catch (error) {
            console.error(error);
            toast.error('Failed to update Nirvahak classes.');
        } finally {
            setLoading(false);
        }
    };

    // Sync state when parent passes a new user (e.g., clicking a different row)
    useEffect(() => {
        setSelectedUser(normalizeUser(initialUser));
    }, [initialUser, normalizeUser]);

    // Build edited user from selected user
    const buildEditedUser = useCallback(() => ({
        ...selectedUser,
        user: {
            ...(selectedUser?.rawData?.user || selectedUser?.user),
            addressDetails: (selectedUser?.rawData?.user || selectedUser?.user)?.addressDetails
                ? [...(selectedUser?.rawData?.user || selectedUser?.user).addressDetails]
                : [{ addressType: 'CURRENT' }],
            additionalAttributes: {
                ...((selectedUser?.rawData?.user || selectedUser?.user)?.additionalAttributes || {}),
            },
        },
        [infoKey]: {
            ...(selectedUser?.rawData?.[infoKey] || selectedUser?.[infoKey]),
            ...(infoKey === 'facultyInfo' || infoKey === 'staffInfo'
                ? {
                    responsibilities: Array.isArray(
                        (selectedUser?.rawData?.[infoKey] || selectedUser?.[infoKey])?.responsibilities
                    )
                        ? [...(selectedUser?.rawData?.[infoKey] || selectedUser?.[infoKey]).responsibilities]
                        : [],
                }
                : {}),
        },
    }), [selectedUser, infoKey]);

    const [editedUser, setEditedUser] = useState(() => buildEditedUser());

    // ── Parent details (Student only) ──
    const [parentDetails, setParentDetails] = useState({
        father: { name: '', email: '', phoneNumber: '', occupation: '', userId: '' },
        mother: { name: '', email: '', phoneNumber: '', occupation: '', userId: '' },
    });

    const fetchParentDetails = useCallback(async () => {
        if (userType !== 'STUDENT') return;
        const parentIds = selectedUser?.studentInfo?.listOfParentIds;
        if (!parentIds?.length) return;

        setLoading(true);
        try {
            const parentPromises = parentIds.map(id => axios.get(`${configData.GET_USER_DETAILS}/${id}`));
            const parentResponses = await Promise.all(parentPromises);
            const parents = parentResponses.map(res => res.data);
            const father = (parents || []).find(p => p?.parentInfo?.parentType === 'FATHER');
            const mother = (parents || []).find(p => p?.parentInfo?.parentType === 'MOTHER');
            setParentDetails({
                father: {
                    name: father?.user?.firstName || '',
                    email: father?.user?.email || '',
                    phoneNumber: father?.user?.phoneNumber || '',
                    occupation: father?.parentInfo?.occupation || '',
                    userId: father?.user?.userId || ''
                },
                mother: {
                    name: mother?.user?.firstName || '',
                    email: mother?.user?.email || '',
                    phoneNumber: mother?.user?.phoneNumber || '',
                    occupation: mother?.parentInfo?.occupation || '',
                    userId: mother?.user?.userId || ''
                },
            });
        } catch (err) {
            console.error('Error fetching parent details:', err);
        } finally {
            setLoading(false);
        }
    }, [userType, selectedUser, axios]);

    useEffect(() => {
        fetchParentDetails();
        setEditedUser(buildEditedUser());
    }, [selectedUser]);

    // ── Tab configurations per user type ──
    const tabConfig = useMemo(() => {
        switch (userType) {
            case 'STUDENT':
                return [
                    { id: 'profile', label: 'Student Profile', permission: MODULES.STUDENT_PROFILE },
                    { id: 'class-schedule', label: 'Schedule', permission: MODULES.CLASS_SCHEDULE },
                    { id: 'fees', label: 'Fees & Payments', permission: MODULES.FEES },
                    { id: 'documents', label: 'Documents', permission: MODULES.DOCUMENTS },
                    { id: 'performance', label: 'Student Performance', permission: MODULES.STUDENT_PERFORMANCE },
                ];
            case 'FACULTY': {
                const fType = selectedUser?.rawData?.facultyInfo?.facultyType || selectedUser?.facultyInfo?.facultyType || selectedUser?.facultyType;
                if (isKvFaculty) {
                    const kvTabs = [];
                    if (fType === 'SAMANVAYKARTA') {
                        kvTabs.push({ id: 'subordinates', label: 'Paryavekshaks' });
                    } else if (fType === 'PARYAVEKSHAK') {
                        kvTabs.push({ id: 'subordinates', label: 'Nirvahaks' });
                    } else if (fType === 'NIRVAHAK') {
                        kvTabs.push({ id: 'students', label: 'Students' });
                    }
                    kvTabs.push({ id: 'profile', label: 'Faculty Profile' });
                    return kvTabs;
                }
                const facultyTabs = [
                    { id: 'profile', label: 'Faculty Profile' },
                    { id: 'classes', label: 'Classes' },
                    { id: 'schedule', label: 'Schedule' },
                    { id: 'documents', label: 'Documents' },
                ];
                return facultyTabs;
            }
            case 'STAFF':
            case 'ADMIN':
                return [
                    { id: 'profile', label: `${userType === 'ADMIN' ? 'Admin' : 'Staff'} Profile` },
                    { id: 'documents', label: 'Documents' },
                ];
            default:
                return [{ id: 'profile', label: 'Profile' }];
        }
    }, [userType, selectedUser, isKvFaculty]);

    const visibleTabs = useMemo(() =>
        tabConfig.filter(tab => !tab.permission || hasPermission(tab.permission, ACTIONS.VIEW)),
        [tabConfig, hasPermission]
    );

    // Auto-switch to Fees tab if returning from payment
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const status = urlParams.get('status');
        if (status && sessionStorage.getItem('pendingPayPhiPayment')) {
            const feesTabIndex = visibleTabs.findIndex(tab => tab.id === 'fees');
            if (feesTabIndex !== -1) {
                setCurrentTab(feesTabIndex);
            }
        }
    }, [visibleTabs]);

    useEffect(() => {
        if (visibleTabs.length > 0 && currentTab >= visibleTabs.length) {
            setCurrentTab(0);
        }
    }, [visibleTabs.length, currentTab]);

    /* ── Handlers ── */
    const handleChangeTab = (_, newValue) => {
        setCurrentTab(newValue);
        setIsEditing(false);
    };

    const handleEditClick = () => {
        setEditedUser(buildEditedUser());
        setIsEditing(true);
    };

    const handleCancelClick = () => {
        setEditedUser(buildEditedUser());
        setIsEditing(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const nameParts = name.split('.');

        setEditedUser(prev => {
            const newState = { ...prev };

            if (nameParts.length === 1) {
                newState[nameParts[0]] = value;
            } else if (nameParts.length === 2) {
                newState[nameParts[0]] = { ...newState[nameParts[0]], [nameParts[1]]: value };
            } else if (nameParts.length === 3) {
                newState[nameParts[0]] = {
                    ...newState[nameParts[0]],
                    [nameParts[1]]: { ...newState[nameParts[0]][nameParts[1]], [nameParts[2]]: value },
                };
            } else if (nameParts.length === 4 && nameParts[1] === 'addressDetails') {
                const index = parseInt(nameParts[2]);
                const field = nameParts[3];
                newState[nameParts[0]] = {
                    ...newState[nameParts[0]],
                    addressDetails: [
                        ...newState[nameParts[0]].addressDetails.slice(0, index),
                        { ...newState[nameParts[0]].addressDetails[index], [field]: value },
                        ...newState[nameParts[0]].addressDetails.slice(index + 1),
                    ],
                };
            }

            // Special: responsibilities as comma-separated
            if (name === `${infoKey}.responsibilities`) {
                const responsibilities = value.split(',').map(item => item.trim()).filter(Boolean);
                newState[infoKey] = { ...newState[infoKey], responsibilities };
            }

            if (name === 'facultyInfo.facultyType') {
                newState.facultyInfo = { ...newState.facultyInfo, supervisorId: '' };
            }

            return newState;
        });
    };

    /* ── Save ── */
    const handleSaveClick = async () => {
        setLoading(true);

        try {
            const currentTime = new Date().toISOString();
            const userId = selectedUser?.rawData?.user?.userId || selectedUser?.user?.userId;
            const rawUser = selectedUser?.rawData?.user || selectedUser?.user || {};

            const dob = editedUser.user.dateOfBirth ? new Date(editedUser.user.dateOfBirth).toISOString() : '';

            const userPayload = {
                ...editedUser.user,
                userId,
                orgId: rawUser.orgId || orgId,
                userType: rawUser.userType || userType,
                roleId: rawUser.roleId || `ROLE_${userType}`,
                userStatus: rawUser.userStatus || 'ACTIVE',
                createdBy: rawUser.createdBy || 'system',
                createdAt: rawUser.createdAt || currentTime,
                lastUpdatedBy: 'system',
                lastUpdatedAt: currentTime,
                lastFourDigitsOfAadhaarNumber: parseInt(editedUser.user.lastFourDigitsOfAadhaarNumber) || null,
                dateOfBirth: dob,
                gender: editedUser.user.gender?.toUpperCase(),
                middleName: editedUser.user.middleName || '',
                // Preserve existing documents (photos, certificates, etc.)
                documentsInfo: rawUser.documentsInfo || [],
                addressDetails: [{
                    addressType: 'CURRENT',
                    addressLine1: editedUser.user.addressDetails?.[0]?.addressLine1 || '',
                    addressLine2: editedUser.user.addressDetails?.[0]?.addressLine2 || '',
                    city: editedUser.user.addressDetails?.[0]?.city || '',
                    state: editedUser.user.addressDetails?.[0]?.state || '',
                    country: editedUser.user.addressDetails?.[0]?.country || 'India',
                    pinCode: editedUser.user.addressDetails?.[0]?.pinCode || '',
                    alternatePhoneNumber: editedUser.user.addressDetails?.[0]?.alternatePhoneNumber || '',
                }],
            };

            // Build type-specific info payload
            let typeInfoPayload = null;
            const rawInfo = selectedUser?.rawData?.[infoKey] || {};

            if (infoKey === 'studentInfo') {
                const admDate = editedUser.studentInfo?.dateOfJoining ? new Date(editedUser.studentInfo.dateOfJoining).toISOString() : '';
                typeInfoPayload = { ...editedUser.studentInfo, dateOfJoining: admDate };
            } else if (infoKey === 'facultyInfo') {
                const joinDate = editedUser.facultyInfo?.joiningDate ? new Date(editedUser.facultyInfo.joiningDate).toISOString() : '';
                typeInfoPayload = {
                    ...editedUser.facultyInfo,
                    userId,
                    joiningDate: joinDate,
                    createdBy: rawInfo.createdBy || 'system',
                    createdAt: rawInfo.createdAt || currentTime,
                    lastUpdatedBy: 'system',
                    lastUpdatedAt: currentTime,
                    responsibilities: editedUser.facultyInfo?.responsibilities?.length > 0 ? editedUser.facultyInfo.responsibilities : [],
                };
            } else if (infoKey === 'staffInfo') {
                const joinDate = editedUser.staffInfo?.joiningDate ? new Date(editedUser.staffInfo.joiningDate).toISOString() : '';
                typeInfoPayload = {
                    ...editedUser.staffInfo,
                    userId,
                    joiningDate: joinDate,
                    createdBy: rawInfo.createdBy || 'system',
                    createdAt: rawInfo.createdAt || currentTime,
                    lastUpdatedBy: 'system',
                    lastUpdatedAt: currentTime,
                    responsibilities: editedUser.staffInfo?.responsibilities?.length > 0 ? editedUser.staffInfo.responsibilities : [],
                    drivingExperienceYears: editedUser.staffInfo?.drivingExperienceYears ? parseInt(editedUser.staffInfo.drivingExperienceYears) : null,
                };
            }

            const updateData = {
                user: userPayload,
                studentInfo: infoKey === 'studentInfo' ? typeInfoPayload : null,
                parentInfo: null,
                staffInfo: infoKey === 'staffInfo' ? typeInfoPayload : null,
                facultyInfo: infoKey === 'facultyInfo' ? typeInfoPayload : null,
            };

            const response = await fetch(`${configData.UPDATE_USER}/${userId}`, {
                method: 'PUT',
                headers: {
                    accept: 'application/hal+json',
                    'Content-Type': 'application/json',
                    Authorization: token,
                },
                body: JSON.stringify(updateData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `API error: ${response.status}`);
            }

            const result = await response.json();

            const updatedUser = {
                ...selectedUser,
                firstName: editedUser.user.firstName,
                lastName: editedUser.user.lastName,
                email: editedUser.user.email,
                phoneNumber: editedUser.user.phoneNumber,
                user: result.user || editedUser.user,
                [infoKey]: result[infoKey] || typeInfoPayload || editedUser[infoKey],
                rawData: {
                    ...selectedUser.rawData,
                    user: result.user || editedUser.user,
                    [infoKey]: result[infoKey] || typeInfoPayload || editedUser[infoKey]
                },
            };

            setSelectedUser(updatedUser);
            if (onUserUpdate) onUserUpdate(updatedUser);
            if (onSelectedUserChange) onSelectedUserChange(updatedUser);
            if (userType === 'STUDENT') await fetchParentDetails();

            const labelMap = { STUDENT: 'Student', FACULTY: 'Faculty', STAFF: 'Staff', ADMIN: 'Admin' };
            toast.success(`${labelMap[userType] || 'User'} information updated successfully!`);
            setIsEditing(false);
        } catch (err) {
            console.error(`Error updating ${userType}:`, err);
            toast.error(`Failed to update: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    /* ── Render Profile Details per type ── */
    const renderProfileDetails = () => {
        const commonProps = {
            selectedUser,
            editedUser,
            isEditing,
            handleEditClick,
            handleSaveClick,
            handleCancelClick,
            handleChange,
            loading,
            orgId,
            axios,
        };

        switch (userType) {
            case 'STUDENT':
                return (
                    <StudentDetails
                        {...commonProps}
                        onSelectedUserChange={onSelectedUserChange || (() => { })}
                        father={parentDetails.father}
                        mother={parentDetails.mother}
                    />
                );
            case 'FACULTY':
                return <FacultyDetails {...commonProps} />;
            case 'STAFF':
            case 'ADMIN':
                return <StaffDetails {...commonProps} />;
            default:
                return <div>Unknown user type</div>;
        }
    };

    /* ── Render Tab Content ── */
    const renderTabContent = () => {
        if (currentTab >= visibleTabs.length) return null;
        const tabId = visibleTabs[currentTab].id;

        switch (tabId) {
            case 'profile':
                return renderProfileDetails();
            case 'class-schedule':
                return <Calendar selectedUser={selectedUser} />;
            case 'fees':
                return <Fees selectedUser={selectedUser} />;
            case 'notifications':
                return <Messages selectedUser={selectedUser} />;
            case 'documents': {
                const activeId = selectedUser?.userId || selectedUser?.user?.userId || selectedUser?.rawData?.user?.userId;
                return <DocumentsTab entityId={activeId} entityType="USER" orgId={orgId} />;
            }
            case 'performance':
                return <StudentPerformanceDashboard selectedUser={selectedUser} />;
            case 'classes':
                return <ClassesTab selectedUser={selectedUser} />;
            case 'schedule':
                return <ClassScheduleTab selectedUser={selectedUser} />;
            case 'subordinates':
                return <SubordinatesTab selectedUser={selectedUser} orgId={orgId} onOpenSubordinate={onOpenSubordinate} onDelete={onDelete} />;
            case 'students':
                return <NirvahakStudentsTab selectedUser={selectedUser} orgId={orgId} onAddClasses={onAddClasses} />;
            default:
                return null;
        }
    };

    /* ── Render ── */
    return (
        <div className="profile-module">
            {/* Premium Profile Header Card - Only for KV Faculty */}
            {isKvFaculty && (
                <div className="premium-profile-header-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div className="profile-header-left" style={{ display: 'flex', alignItems: 'center' }}>
                        <div className="profile-header-avatar">
                            {getInitials(firstName, lastName, email)}
                        </div>
                        <div className="profile-header-details">
                            <span className="profile-header-role">{fType === 'SAMANVAYKARTA' ? 'SAMANVAYAKARTA' : fType}</span>
                            <h2 className="profile-header-name">{name}</h2>
                            <div className="profile-header-sub">
                                {displayClasses && <span>{displayClasses}</span>}
                                {email && (
                                    <>
                                        {displayClasses && <span className="profile-header-bullet">•</span>}
                                        <span>{email}</span>
                                    </>
                                )}
                                {phoneNumber && (
                                    <>
                                        {(displayClasses || email) && <span className="profile-header-bullet">•</span>}
                                        <span>{phoneNumber}</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                </div>
            )}

            <AppBar position="static" className="profile-tab-bar" style={{ boxShadow: 'none' }}>
                <Tabs value={currentTab} onChange={handleChangeTab}>
                    {visibleTabs.map((tab) => (
                        <Tab key={tab.id} label={tab.label} />
                    ))}
                </Tabs>
            </AppBar>

            <div className="profile-tab-content">
                {loading ? (
                    <div className="profile-loading">
                        <CircularProgress />
                    </div>
                ) : (
                    renderTabContent()
                )}
            </div>


        </div>
    );
}

export default UserProfileModule;
